// Utility functions for standardized mathematical calculations
// with proper validation and error handling

/**
 * REFACTORED: Simple balance calculations using new financial system
 * No more complex available balance vs total balance logic
 */

/**
 * Safely converts a value to a number with validation
 * @param {any} value - The value to convert
 * @param {number} defaultValue - Default value if conversion fails
 * @returns {number} - The converted number or defaultValue
 */
export const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

/**
 * Validates if a number is valid (not NaN, not infinite)
 * @param {number} num - The number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidNumber = (num) => {
  return typeof num === 'number' && !isNaN(num) && isFinite(num);
};

/**
 * REFACTORED: Simple available balance calculation
 * Now just returns total balance - no deductions
 * @param {number} totalBalance - Total account balance
 * @returns {number} - Available balance (same as total balance)
 */
export const calculateAvailableBalance = (totalBalance) => {
  const balance = safeNumber(totalBalance);
  
  if (!isValidNumber(balance)) {
    console.warn('Invalid number in calculateAvailableBalance:', { totalBalance });
    return 0;
  }
  
  // Simple system: available balance = total balance
  return balance;
};

/**
 * REFACTORED: Simple ending balance calculation
 * Now just returns current balance - no complex calculations
 * @param {number} currentBalance - Current balance
 * @returns {number} - Ending balance (same as current balance)
 */
export const calculateEndingBalance = (currentBalance) => {
  const balance = safeNumber(currentBalance);
  
  if (!isValidNumber(balance)) {
    console.warn('Invalid numbers in calculateEndingBalance:', { currentBalance });
    return 0;
  }
  
  // Simple system: ending balance = current balance
  return balance;
};

/**
 * Formats currency with validation
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount) => {
  const num = safeNumber(amount);
  
  if (!isValidNumber(num)) {
    console.warn('Invalid amount in formatCurrency:', amount);
    return '$0.00';
  }
  
  return `$${num.toFixed(2)}`;
};

/**
 * Calculates percentage with validation
 * @param {number} part - Part value
 * @param {number} total - Total value
 * @returns {number} - Percentage (0-100)
 */
export const calculatePercentage = (part, total) => {
  const partNum = safeNumber(part);
  const totalNum = safeNumber(total);
  
  if (!isValidNumber(partNum) || !isValidNumber(totalNum) || totalNum === 0) {
    return 0;
  }
  
  return Math.min(100, Math.max(0, (partNum / totalNum) * 100));
};

/**
 * Validates trade financial data
 * @param {Object} tradeData - Trade financial data
 * @returns {Object} - Validation result with isValid flag and errors array
 */
export const validateTradeFinancials = (tradeData) => {
  const errors = [];
  
  if (!tradeData) {
    return { isValid: false, errors: ['Trade data is required'] };
  }
  
  const requiredFields = ['original_balance', 'site_fee', 'delivery_fees', 'total_debits_excluding_hold'];
  
  for (const field of requiredFields) {
    if (!isValidNumber(safeNumber(tradeData[field]))) {
      errors.push(`Invalid ${field}: ${tradeData[field]}`);
    }
  }
  
  // Validate that total debits equals site fee + delivery fees
  const calculatedTotal = safeNumber(tradeData.site_fee) + safeNumber(tradeData.delivery_fees);
  const storedTotal = safeNumber(tradeData.total_debits_excluding_hold);
  
  if (Math.abs(calculatedTotal - storedTotal) > 0.01) {
    errors.push(`Total debits mismatch: calculated ${calculatedTotal}, stored ${storedTotal}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Error boundary for mathematical operations
 * @param {Function} operation - Mathematical operation to perform
 * @param {any} defaultValue - Default value if operation fails
 * @returns {any} - Result of operation or defaultValue
 */
export const safeMathOperation = (operation, defaultValue = 0) => {
  try {
    return operation();
  } catch (error) {
    console.error('Mathematical operation failed:', error);
    return defaultValue;
  }
};

/**
 * Calculate tiered trade fee based on item value
 * @param {number} itemValue - The value of the item being borrowed
 * @returns {number} - The tiered fee amount
 */
export const calculateTieredFee = (itemValue) => {
  const value = safeNumber(itemValue);
  
  if (!isValidNumber(value)) {
    return 5.0; // Default fee for invalid values
  }
  
  // Tiered pricing based on item value
  if (value <= 99) {
    return 5.0;
  } else if (value <= 499) {
    return 10.0;
  } else if (value <= 999) {
    return 10.0;
  } else { // $1000+
    return 20.0;
  }
};
